import Attend from "@/Components/WinningPage/Attend";
import Guideline from "@/Components/WinningPage/Guideline";
import ImageUrl from "@/Components/WinningPage/ImageUrl";
import Reservation from "@/Components/WinningPage/Reservation";
import Review from "@/Components/WinningPage/Review";
import { ModelCampaignWinnerGuidelinesInfoDTO } from "@models/ModelCampaignWinnerGuidelinesInfoDTO";
import { WgInfoGet200Response } from "@models/WgInfoGet200Response";
import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BASE_URL } from "@/Store/Action";
import Swal from "sweetalert2";
import Address from "@/Components/WinningPage/Address";

function WinningPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { secretkey } = useParams<{ secretkey: string }>();
  const [guidelineData, setGuidelineData] =
    useState<ModelCampaignWinnerGuidelinesInfoDTO | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);

  // URL 파라미터에서 step 값 가져오기 (기본값: 1)
  const step = parseFloat(searchParams.get("step") || "1");

  // Secret key 유효성 검증
  const isValidSecretKey = useCallback((key: string | undefined): boolean => {
    return !!(key && key.length > 0 && /^[a-zA-Z0-9-_]+$/.test(key));
  }, []);

  // API로 가이드라인 데이터 가져오기
  const {
    data: fetchedGuidelineData,
    isLoading,
    error,
    refetch,
  } = useQuery<ModelCampaignWinnerGuidelinesInfoDTO | null>({
    queryKey: ["campaign-wg", secretkey],
    queryFn: async () => {
      if (!isValidSecretKey(secretkey)) {
        throw new Error("링크가 유효하지 않습니다");
      }

      const res = await fetch(`${BASE_URL}/wg/info?secret_key=${secretkey}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        if (res.status === 404) {
          throw new Error("가이드라인을 찾을 수 없습니다.");
        } else if (res.status === 403) {
          throw new Error("접근 권한이 없습니다.");
        }
        throw new Error("가이드라인 정보를 불러오는데 실패했습니다.");
      }

      const data: WgInfoGet200Response = await res.json();
      if (!data?.data) {
        throw new Error("서버 오류가 발생했습니다");
      }
      return data.data;
    },
    enabled: isValidSecretKey(secretkey),
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // URL 업데이트 함수 (중복 클릭 방지)
  const updateStep = useCallback(
    (newStep: number) => {
      if (isNavigating) return;

      setIsNavigating(true);
      const newParams = new URLSearchParams(searchParams);
      newParams.set("step", newStep.toString());
      setSearchParams(newParams);

      // 300ms 후 네비게이션 플래그 해제
      setTimeout(() => setIsNavigating(false), 300);
    },
    [searchParams, setSearchParams, isNavigating]
  );

  // API 데이터가 로드되면 guidelineData 설정
  useEffect(() => {
    if (fetchedGuidelineData && !guidelineData) {
      setGuidelineData(fetchedGuidelineData);
    }
  }, [fetchedGuidelineData, guidelineData]);

  // 각 스텝 접근 가능 여부 확인 (handleGuidelineNext 로직 기반)
  const getStepAccessibility = () => {
    if (!guidelineData)
      return {
        canAccess1_5: false,
        canAccess2: false,
        canAccess3: false,
        canAccess4: false,
      };

    const needsReservation =
      guidelineData.is_reservation && !guidelineData.reservation_dtm;

    return {
      canAccess2: needsReservation ? false : true, // 예약 필요하면 1.5를 거쳐야 함
      canAccess1_5: needsReservation, // 예약이 필요한 경우만 1.5 접근 가능
      canAccess3: guidelineData.is_attend === true, // 출석 완료 시 3 접근 가능
      canAccess4: guidelineData.is_attend === true, // 출석 완료 시 4도 접근 가능 (3은 단순 확인)
    };
  };

  const accessibility = getStepAccessibility();

  // Step 유효성 검증 및 리다이렉션
  useEffect(() => {
    // 유효한 step 값인지 확인
    const validSteps = [1, 1.5, 2, 3, 4];
    if (!validSteps.includes(step)) {
      updateStep(1);
      return;
    }

    // 데이터 로딩이 완료된 후에만 접근 권한 검증
    if (!isLoading && guidelineData) {
      const accessibility = getStepAccessibility();

      // Step 1.5: 예약이 필요하지 않은 경우 접근 불가
      if (step === 1.5 && !accessibility.canAccess1_5) {
        updateStep(2);
        return;
      }

      // Step 2: 예약이 필요한데 하지 않은 경우 접근 불가
      if (step === 2 && !accessibility.canAccess2) {
        updateStep(1.5);
        return;
      }

      // Step 3, 4: 출석하지 않은 경우 접근 불가
      if ((step === 3 || step === 4) && !accessibility.canAccess3) {
        updateStep(accessibility.canAccess2 ? 2 : 1.5);
        return;
      }
    }
  }, [step, guidelineData, isLoading]);

  // 브라우저 뒤로가기/앞으로가기 처리
  useEffect(() => {
    const handlePopState = () => {
      const currentStep = parseFloat(searchParams.get("step") || "1");

      // Step이 변경되었을 때 유효성 검증이 다시 실행되도록 함
      if (currentStep !== step && guidelineData) {
        const accessibility = getStepAccessibility();

        // 잘못된 접근인 경우 경고 표시
        if (
          (currentStep === 1.5 && !accessibility.canAccess1_5) ||
          (currentStep === 2 && !accessibility.canAccess2) ||
          ((currentStep === 3 || currentStep === 4) &&
            !accessibility.canAccess3)
        ) {
          Swal.fire({
            icon: "warning",
            title: "아직 이 단계로 이동할 수 없습니다",
            text: "이전 단계를 완료해주세요.",
            confirmButtonText: "확인",
            customClass: {
              confirmButton:
                "bg-[#0F1E6C] text-white px-4 py-2 rounded-full font-semibold",
            },
            buttonsStyling: false,
          });
        }
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [searchParams, step, guidelineData]);

  const handleGuidelineNext = (data: ModelCampaignWinnerGuidelinesInfoDTO) => {
    setGuidelineData(data);
    if (!data.is_reservation || data.reservation_dtm) {
      updateStep(2);
    } else {
      updateStep(1.5);
    }
  };

  return (
    <div className="max-w-xl mx-auto pb-28">
      <div className="pb-5 justify-center items-center ">
        {/* Stepper */}
        <div className="mx-auto p-4">
          <div className="grid grid-cols-4 gap-0 w-full mx-auto mb-2 pt-2">
            {[1, 2, 3, 4].map((stepNum, index) => {
              const isActive =
                (stepNum === 1 && (step === 1 || step === 1.5)) ||
                (stepNum === 2 && step === 2) ||
                (stepNum === 3 && step === 3) ||
                (stepNum === 4 && step === 4);

              // Adjust isCompleted to consider step 1.5 as part of step 1
              const isCompleted =
                (stepNum === 1 && step > 1.5) ||
                (stepNum === 2 && step > 2) ||
                (stepNum === 3 && step > 3);

              const isLast = index === 3;

              const handleStepClick = () => {
                if (isNavigating) return; // 중복 클릭 방지

                // 스텝 1은 항상 이동 가능
                if (stepNum === 1) {
                  updateStep(1);
                  return;
                }

                // 스텝 2 클릭 시
                if (stepNum === 2) {
                  if (!accessibility.canAccess2) {
                    Swal.fire({
                      icon: "warning",
                      title: "아직 접근할 수 없는 단계입니다",
                      text: "먼저 날짜와 시간을 예약해 주세요.",
                      confirmButtonText: "확인",
                      buttonsStyling: false,
                      customClass: {
                        confirmButton:
                          "bg-yellow-400 text-white px-8 py-2 rounded-full font-semibold",
                      },
                    });

                    return;
                  }
                  updateStep(2);
                  return;
                }

                // 스텝 3 클릭 시
                if (stepNum === 3) {
                  if (!accessibility.canAccess3) {
                    Swal.fire({
                      icon: "warning",
                      title: "아직 접근할 수 없는 단계입니다",
                      text: "매장 방문 후 체험 확인을 완료해 주세요.",
                      confirmButtonText: "확인",
                      customClass: {
                        confirmButton:
                          "bg-yellow-400 text-white px-8 py-2 rounded-full font-semibold",
                      },
                    });
                    return;
                  }
                  updateStep(3);
                  return;
                }

                // 스텝 4 클릭 시
                if (stepNum === 4) {
                  if (!accessibility.canAccess4) {
                    Swal.fire({
                      icon: "warning",
                      title: "아직 접근할 수 없는 단계입니다",
                      text: "순서대로 진행해 주세요. 이전 단계를 먼저 완료해야 합니다.",
                      confirmButtonText: "확인",
                      customClass: {
                        confirmButton:
                          "bg-yellow-400 text-white px-8 py-2 rounded-full font-semibold",
                      },
                    });
                    return;
                  }
                  updateStep(4);
                  return;
                }
              };

              return (
                <div
                  key={stepNum}
                  className="flex items-center justify-center relative"
                >
                  {isActive && (
                    <div className="absolute w-7 h-7 bg-[#0F1E6C] rounded-full animate-ping opacity-30"></div>
                  )}
                  <div
                    onClick={handleStepClick}
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs z-10 transition-all duration-200 ${
                      // 클릭 가능한 조건 체크
                      stepNum === 1 ||
                      (stepNum === 2 && accessibility.canAccess2) ||
                      (stepNum === 3 && accessibility.canAccess3) ||
                      (stepNum === 4 && accessibility.canAccess4)
                        ? "cursor-pointer hover:scale-110"
                        : "cursor-not-allowed"
                    } ${
                      isActive
                        ? "bg-[#0F1E6C] text-white"
                        : isCompleted
                        ? "bg-[#0F1E6C] text-white"
                        : "bg-gray-200 text-white"
                    }`}
                  >
                    {stepNum}
                  </div>
                  {!isLast && (
                    <div
                      className={`absolute top-1/2 left-1/2 w-full h-px transform -translate-y-1/2 ${
                        isCompleted ? "bg-[#0F1E6C]" : "bg-gray-300"
                      }`}
                    ></div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Step Titles */}
          <div className="grid grid-cols-4 gap-0 w-full mx-auto text-[11px] text-gray-600">
            <div
              className={`text-center ${
                step === 1 || step === 1.5
                  ? "text-[#0F1E6C] font-bold"
                  : step > 1
                  ? "text-gray-300"
                  : "text-gray-300"
              }`}
            >
              체험 안내
            </div>
            <div
              className={`text-center ${
                step === 2
                  ? "text-[#0F1E6C] font-bold"
                  : step > 2
                  ? "text-gray-300"
                  : "text-gray-300"
              }`}
            >
              {fetchedGuidelineData?.campaign_type === "배송형"
                ? "배송지 입력"
                : "체험 초대"}
            </div>
            <div
              className={`text-center ${
                step === 3
                  ? "text-[#0F1E6C] font-bold"
                  : step > 3
                  ? "text-gray-300"
                  : "text-gray-300"
              }`}
            >
              협찬 배너
            </div>
            <div
              className={`text-center ${
                step === 4 ? "text-[#0F1E6C] font-bold" : "text-gray-300"
              }`}
            >
              리뷰 작성
            </div>
          </div>
        </div>

        {/* Content by step */}
        <div className="px-5 pt-4">
          {/* Secret key 유효성 검증 실패 */}
          {!isValidSecretKey(secretkey) && (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="text-red-500 mb-4">
                <svg
                  className="w-16 h-16 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h2 className="text-lg font-bold mb-2">잘못된 접근입니다</h2>
              <p className="text-sm text-gray-600">유효하지 않은 링크입니다.</p>
            </div>
          )}

          {/* 에러 발생 시 */}
          {error && !isLoading && (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="text-red-500 mb-4">
                <svg
                  className="w-16 h-16 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-lg font-bold mb-2">오류가 발생했습니다</h2>
              <p className="text-sm text-gray-600 mb-4">
                {(error as Error).message}
              </p>
              <button
                onClick={() => refetch()}
                className="px-4 py-2 bg-[#0F1E6C] text-white rounded-full text-sm hover:bg-[#0a1347] transition-colors"
              >
                다시 시도
              </button>
            </div>
          )}

          {/* 로딩 중 */}
          {isLoading && !error && (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-2"></div>
              <span className="text-sm text-gray-600">
                캠페인 정보를 불러오고 있습니다...
              </span>
            </div>
          )}

          {!isLoading &&
            !error &&
            isValidSecretKey(secretkey) &&
            step === 1 && (
              <Guideline
                onNext={handleGuidelineNext}
                guideline={guidelineData}
              />
            )}

          {!isLoading &&
            !error &&
            isValidSecretKey(secretkey) &&
            step === 1.5 &&
            guidelineData?.is_reservation === true &&
            guidelineData?.reservation_dtm !== null && (
              <Reservation
                guideline={guidelineData}
                onPrev={() => updateStep(1)}
                onNext={(data) => {
                  setGuidelineData(data);
                  updateStep(2);
                }}
              />
            )}
          {fetchedGuidelineData?.campaign_type === "배송형" ? (
            <>
              {!isLoading &&
                !error &&
                isValidSecretKey(secretkey) &&
                step === 2 &&
                guidelineData && (
                  // Use state from tanstack query for the data
                  <Address
                    guideline={fetchedGuidelineData}
                    onPrev={() =>
                      updateStep(
                        fetchedGuidelineData?.is_reservation === false ||
                          fetchedGuidelineData?.reservation_dtm
                          ? 1
                          : 1.5
                      )
                    }
                    onNext={() => {
                      // 출석 완료 시에만 다음 단계로
                      if (accessibility.canAccess3) {
                        updateStep(3);
                      }
                    }}
                  />
                )}
            </>
          ) : (
            <>
              {!isLoading &&
                !error &&
                isValidSecretKey(secretkey) &&
                step === 2 &&
                guidelineData && (
                  <Attend
                    guideline={guidelineData}
                    onPrev={() =>
                      updateStep(
                        guidelineData?.is_reservation === false ||
                          guidelineData?.reservation_dtm
                          ? 1
                          : 1.5
                      )
                    }
                    onNext={() => {
                      // 출석 완료 시에만 다음 단계로
                      if (accessibility.canAccess3) {
                        updateStep(3);
                      }
                    }}
                  />
                )}
            </>
          )}

          {!isLoading &&
            !error &&
            isValidSecretKey(secretkey) &&
            step === 3 &&
            guidelineData && (
              <ImageUrl
                guideline={guidelineData}
                onPrev={() => updateStep(2)}
                onNext={() => {
                  // 협찬배너 확인 완료 시에만 다음 단계로
                  if (accessibility.canAccess4) {
                    updateStep(4);
                  }
                }}
              />
            )}

          {!isLoading &&
            !error &&
            isValidSecretKey(secretkey) &&
            step === 4 &&
            guidelineData && (
              <Review
                guideline={guidelineData}
                onPrev={() => updateStep(3)}
                onNext={() => updateStep(4)}
              />
            )}
        </div>
      </div>
    </div>
  );
}

export default WinningPage;
